interface PdfData {
  formData: {
    company: string
    name: string
    email: string
    phone: string
  }
  pricing: {
    oneTime: { min: number; max: number }
    monthly: number
    yearly: number
  }
  includedFeatures: Array<{ text: string; tooltip: string }>
  allAnswers: Array<{ question: string; answer: string; price?: string }>
}

// Price mappings for display
const PAGE_PRICES: Record<string, number> = {
  Home: 0,
  Diensten: 350,
  'Over ons': 350,
  Contact: 350,
  'Team / medewerkers': 350,
  'Klantportaal / login': 1500,
  'Blog / kennisbank': 500,
  Vacatures: 500,
  Overig: 350,
}

const REGULATION_PRICES: Record<string, number> = {
  'AFM / Wft-communicatie': 150,
  'Privacy & AVG': 50,
  'Disclaimer / risicovermelding': 50,
  Cookiebeleid: 50,
  'Geen specifieke eisen': 0,
  Overig: 100,
}

const getAnswerPrice = (question: string, answer: string): string => {
  // Org size - show multiplier
  if (question.includes('Hoe groot is uw organisatie')) {
    if (answer.includes('Zelfstandig')) return '×0.75'
    if (answer.includes('2–5')) return '×1.00'
    if (answer.includes('6–15')) return '×1.25'
    if (answer.includes('15+')) return '×1.50'
    return '–'
  }

  // Website type - show base price
  if (question.includes('Wat voor type website')) {
    if (answer.includes('Onepager')) return '€750'
    if (answer.includes('Webshop')) return '€3.000'
    if (answer.includes('Normale website')) return '€1.200'
    return '–'
  }

  // Pages - show price per page
  if (question.includes("Welke pagina's verwacht u")) {
    const pages = answer.split(', ')
    const prices = pages.map((page) => {
      const trimmedPage = page.trim()
      for (const [key, price] of Object.entries(PAGE_PRICES)) {
        if (trimmedPage.includes(key)) {
          return price > 0 ? `€${price}` : '€0'
        }
      }
      return '€350'
    })
    return prices.join(', ')
  }

  // Regulations - show price per item
  if (question.includes('specifieke regels voldoen')) {
    const regs = answer.split(', ')
    const prices = regs.map((reg) => {
      const trimmedReg = reg.trim()
      for (const [key, price] of Object.entries(REGULATION_PRICES)) {
        if (trimmedReg.includes(key)) {
          return price > 0 ? `€${price}` : '€0'
        }
      }
      return '€100'
    })
    return prices.join(', ')
  }

  // SEO - show setup price
  if (question.includes('vindbaarheid in Google')) {
    if (answer.includes('Zeer belangrijk')) return '€1.000'
    if (answer.includes('Belangrijk')) return '€500'
    if (answer.includes('Enigszins')) return '€250'
    if (answer.includes('Niet belangrijk')) return '€0'
    return '–'
  }

  // Content writing
  if (question.includes('teksten door een specialist')) {
    if (answer.includes('Volledig laten schrijven')) return '€150/pagina'
    if (answer.includes('Samen herschrijven')) return '€50'
    if (answer.includes('zelf aan')) return '€0'
    return '–'
  }

  // Hosting - show monthly cost
  if (question.includes('hosting, onderhoud en beveiliging')) {
    if (answer.includes('Volledig ontzorgd')) return '€20+ p/m'
    return '€0 p/m'
  }

  // No price for these questions
  return '–'
}

export const generateScanPdf = (data: PdfData) => {
  const { formData, pricing, includedFeatures, allAnswers } = data
  const date = new Date().toLocaleDateString('nl-NL', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })

  const featuresHtml = includedFeatures
    .map(
      (f) => `
      <tr>
        <td style="padding: 8px 12px; border-bottom: 1px solid #E5E7EB; font-size: 13px; color: #111827;">
          <span style="color: #007AFF; font-weight: bold; margin-right: 8px;">✓</span>${f.text}
        </td>
      </tr>`,
    )
    .join('')

  const answersHtml = allAnswers
    .map((a, i) => {
      const price = getAnswerPrice(a.question, a.answer)
      return `
      <tr style="background-color: ${i % 2 === 0 ? '#F9FAFB' : '#FFFFFF'};">
        <td style="padding: 10px 12px; border-bottom: 1px solid #E5E7EB; font-size: 12px; color: #6B7280; font-weight: 600; width: 35%; vertical-align: top;">${a.question}</td>
        <td style="padding: 10px 12px; border-bottom: 1px solid #E5E7EB; font-size: 12px; color: #111827; width: 45%;">${a.answer}</td>
        <td style="padding: 10px 12px; border-bottom: 1px solid #E5E7EB; font-size: 12px; color: #007AFF; font-weight: 600; width: 20%; text-align: right;">${price}</td>
      </tr>`
    })
    .join('')

  const monthlyDisplay = pricing.monthly > 0 ? `€${pricing.monthly}` : '€0'

  const monthlySection =
    pricing.monthly > 0
      ? `
      <div style="flex: 1; background: #F9FAFB; border-radius: 8px; padding: 16px; border: 1px solid #E5E7EB;">
        <div style="font-size: 10px; text-transform: uppercase; letter-spacing: 1px; color: #6B7280; font-weight: 700; margin-bottom: 6px;">Maandelijks</div>
        <div style="font-size: 22px; font-weight: 800; color: #111827;">€${pricing.monthly} <span style="font-size: 13px; font-weight: 400; color: #6B7280;">p/m</span></div>
      </div>`
      : ''

  const html = `<!DOCTYPE html>
<html lang="nl">
<head>
  <meta charset="UTF-8">
  <title>Prijsscan – ${formData.company}</title>
  <style>
    @media print {
      body { margin: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .no-print { display: none !important; }
      @page { margin: 15mm 12mm; size: A4; }
    }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #111827; background: #fff; }
    .page { max-width: 780px; margin: 0 auto; padding: 20px; }
    .header { background: linear-gradient(135deg, #007AFF, #005FCC); color: white; padding: 32px 36px; border-radius: 12px; margin-bottom: 28px; }
    .header h1 { font-size: 28px; font-weight: 800; margin-bottom: 4px; letter-spacing: -0.5px; }
    .header p { font-size: 15px; opacity: 0.85; }
    .meta { display: flex; gap: 24px; flex-wrap: wrap; margin-bottom: 28px; padding: 16px 20px; background: #F9FAFB; border-radius: 8px; border: 1px solid #E5E7EB; }
    .meta-item { font-size: 13px; color: #6B7280; }
    .meta-item strong { color: #111827; display: block; margin-top: 2px; }
    .pricing-grid { display: flex; gap: 12px; margin-bottom: 28px; flex-wrap: wrap; }
    .pricing-grid > div { flex: 1; min-width: 200px; }
    .pricing-main { background: linear-gradient(135deg, #007AFF, #005FCC); color: white; border-radius: 8px; padding: 20px; }
    .pricing-main .label { font-size: 10px; text-transform: uppercase; letter-spacing: 1px; opacity: 0.8; font-weight: 700; margin-bottom: 6px; }
    .pricing-main .amount { font-size: 26px; font-weight: 800; }
    .pricing-main .desc { font-size: 12px; opacity: 0.75; margin-top: 4px; }
    .section-title { font-size: 18px; font-weight: 700; color: #111827; margin-bottom: 12px; padding-bottom: 8px; border-bottom: 2px solid #007AFF; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 28px; }
    .note { background: #FFFBEB; border-left: 4px solid #F59E0B; border-radius: 0 8px 8px 0; padding: 14px 18px; margin-bottom: 28px; font-size: 13px; color: #92400E; }
    .note strong { display: block; margin-bottom: 2px; color: #78350F; }
    .footer { text-align: center; padding-top: 20px; border-top: 1px solid #E5E7EB; font-size: 11px; color: #9CA3AF; margin-top: 12px; }
    .footer a { color: #007AFF; text-decoration: none; }
    .print-btn { display: block; margin: 0 auto 24px; padding: 12px 32px; background: #007AFF; color: white; border: none; border-radius: 8px; font-size: 15px; font-weight: 600; cursor: pointer; }
    .print-btn:hover { background: #005FCC; }
  </style>
</head>
<body>
  <div class="page">
    <button class="print-btn no-print" onclick="window.print()">⬇ Download als PDF</button>

    <div class="header">
      <h1>Prijsscan Resultaat</h1>
      <p>Persoonlijk plan voor ${formData.company}</p>
    </div>

    <div class="meta">
      <div class="meta-item">Naam <strong>${formData.name || '–'}</strong></div>
      <div class="meta-item">E-mail <strong>${formData.email || '–'}</strong></div>
      <div class="meta-item">Telefoon <strong>${formData.phone || 'Niet opgegeven'}</strong></div>
      <div class="meta-item">Datum <strong>${date}</strong></div>
    </div>

    <div class="pricing-grid">
      <div class="pricing-main">
        <div class="label">Geschatte investering</div>
        <div class="amount">€${pricing.oneTime.min.toLocaleString('nl-NL')} – €${pricing.oneTime.max.toLocaleString('nl-NL')}</div>
        <div class="desc">Websitebouw, design, maatwerk en implementatie</div>
      </div>
      ${monthlySection}
      <div style="flex: 1; background: #F9FAFB; border-radius: 8px; padding: 16px; border: 1px solid #E5E7EB;">
        <div style="font-size: 10px; text-transform: uppercase; letter-spacing: 1px; color: #6B7280; font-weight: 700; margin-bottom: 6px;">Jaarlijks</div>
        <div style="font-size: 22px; font-weight: 800; color: #111827;">€${pricing.yearly} <span style="font-size: 13px; font-weight: 400; color: #6B7280;">p/j</span></div>
      </div>
    </div>

    <div class="note">
      <strong>Dit is een indicatie</strong>
      In de praktijk werken we deze prijs vaak naar beneden op basis van slimme keuzes en efficiënte werkwijzen.
    </div>

    <h2 class="section-title">Wat is inbegrepen</h2>
    <table>${featuresHtml}</table>

    <h2 class="section-title">Uw antwoorden</h2>
    <table>
      <thead>
        <tr>
          <th style="padding: 10px 12px; text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; color: white; background: #007AFF; border-radius: 6px 0 0 0;">Vraag</th>
          <th style="padding: 10px 12px; text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; color: white; background: #007AFF;">Antwoord</th>
          <th style="padding: 10px 12px; text-align: right; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; color: white; background: #007AFF; border-radius: 0 6px 0 0;">Prijs</th>
        </tr>
      </thead>
      <tbody>${answersHtml}</tbody>
    </table>

    <div class="footer">
      <p><strong style="color: #111827;">Noord Digital</strong></p>
      <p style="margin-top: 4px;"><a href="https://noorddigital.nl">www.noorddigital.nl</a> · info@noorddigital.nl</p>
      <p style="margin-top: 8px;">Dit document is gegenereerd op ${date}</p>
    </div>
  </div>
</body>
</html>`

  const printWindow = window.open('', '_blank')
  if (printWindow) {
    printWindow.document.write(html)
    printWindow.document.close()
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.focus()
      }, 250)
    }
  }
}
